import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { CodeBlock } from '@/components/ui/code-block'
import { fetchAndParseSpec, type HttpMethod, type SpecEndpoint, type SpecParameter, type SpecProperty } from '@/lib/openapi-spec'

export const Route = createFileRoute('/$lang/docs/api-reference')({
  staticData: {
    title: 'API Reference — PhoneSalad Docs',
    description: 'Complete reference for the PhoneSalad Agent API. All endpoints, parameters, and response shapes.',
    sitemap: { priority: '0.8', changefreq: 'monthly' },
  },
  component: ApiReferencePage,
})

// ── Primitive display components ──────────────────────────────────────────────

function MethodBadge({ method }: { method: HttpMethod }) {
  const styles: Record<HttpMethod, string> = {
    get: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    post: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    put: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    patch: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    delete: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  }
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold font-mono ${styles[method]}`}>
      {method.toUpperCase()}
    </span>
  )
}

function EndpointBlock({ method, path }: { method: HttpMethod; path: string }) {
  return (
    <div className="flex items-center gap-2.5 bg-muted/40 rounded-lg px-4 py-2.5 my-4 border border-border/50 font-mono text-sm">
      <MethodBadge method={method} />
      <span className="text-foreground">{path}</span>
    </div>
  )
}

function ParamTable({ rows }: { rows: Array<{ name: string; type: string; required: boolean; description?: string; default?: unknown; example?: unknown }> }) {
  if (rows.length === 0) return null
  return (
    <div className="rounded-xl border border-border/60 overflow-hidden my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/40 border-b border-border/50">
            <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">Parameter</th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">Type</th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">Required</th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.name} className="border-t border-border/30">
              <td className="px-4 py-2.5 font-mono text-xs text-foreground whitespace-nowrap">{row.name}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{row.type}</td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{row.required ? 'Yes' : 'No'}</td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">
                {row.description}
                {row.default !== undefined && (
                  <span className="ml-1 text-muted-foreground/70">Default: <code className="font-mono">{String(row.default)}</code></span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ResponseTable({ responses }: { responses: SpecEndpoint['responses'] }) {
  if (responses.length === 0) return null
  return (
    <div className="rounded-xl border border-border/60 overflow-hidden my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/40 border-b border-border/50">
            <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">Status</th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground">Description</th>
          </tr>
        </thead>
        <tbody>
          {responses.map(r => (
            <tr key={r.status} className="border-t border-border/30">
              <td className="px-4 py-2.5 font-mono text-xs font-semibold text-foreground">{r.status}</td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Endpoint section ──────────────────────────────────────────────────────────

function EndpointSection({ endpoint }: { endpoint: SpecEndpoint }) {
  const slug = endpoint.operationId
  const queryParams = endpoint.parameters.filter((p: SpecParameter) => p.in === 'query')
  const pathParams = endpoint.parameters.filter((p: SpecParameter) => p.in === 'path')
  const bodyProps: SpecProperty[] = endpoint.requestBody?.properties ?? []

  return (
    <div className="mb-10">
      <h3
        id={slug}
        className="text-lg font-semibold text-foreground mt-8 mb-1 scroll-mt-24"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        {endpoint.summary}
      </h3>
      {endpoint.description && (
        <p className="text-sm text-muted-foreground mb-2 leading-relaxed">{endpoint.description}</p>
      )}
      <EndpointBlock method={endpoint.method} path={endpoint.path} />

      {pathParams.length > 0 && (
        <>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 mt-4">Path parameters</p>
          <ParamTable rows={pathParams} />
        </>
      )}
      {queryParams.length > 0 && (
        <>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 mt-4">Query parameters</p>
          <ParamTable rows={queryParams} />
        </>
      )}
      {bodyProps.length > 0 && (
        <>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 mt-4">Request body</p>
          <ParamTable rows={bodyProps} />
        </>
      )}
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 mt-4">Responses</p>
      <ResponseTable responses={endpoint.responses} />
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2
      id={id}
      className="text-2xl font-bold text-foreground mt-12 mb-5 pt-8 border-t border-border/50 scroll-mt-24 first:mt-0 first:pt-0 first:border-t-0"
      style={{ fontFamily: 'var(--font-heading)' }}
    >
      {children}
    </h2>
  )
}

function ApiReferencePage() {
  const { data: spec, isLoading, error } = useQuery({
    queryKey: ['openapi-spec'],
    queryFn: () => fetchAndParseSpec('/docs/openapi.yaml'),
    staleTime: Infinity,
  })

  return (
    <div className="max-w-[720px]">
      {/* Page header */}
      <div className="mb-10">
        <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-3">Reference</p>
        <h1 className="text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          API Reference
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          Complete reference for all Agent API endpoints. All requests require a Bearer token
          and JSON bodies.
        </p>
      </div>

      {isLoading && (
        <div className="space-y-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">Failed to load API spec.</p>
      )}

      {spec && (
        <>
          {/* Base URL */}
          {spec.servers[0] && (
            <div className="rounded-xl border border-border/60 bg-muted/30 px-5 py-4 mb-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Base URL</p>
              <code className="font-mono text-sm text-foreground">{spec.servers[0].url}</code>
            </div>
          )}

          {/* Authentication */}
          <H2 id="authentication">Authentication</H2>
          <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
            All API requests (except <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">/agent/voices</code>) require
            a valid API key passed as a Bearer token in the <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">Authorization</code> header.
            Generate keys in your <a href="/account/api" className="text-primary underline underline-offset-2 hover:text-primary/80">Account → API settings</a>.
          </p>
          <CodeBlock code="Authorization: Bearer ps_live_your_api_key_here" collapsedLines={30} />

          {/* Endpoints by tag */}
          {spec.tags.map(tag => {
            const endpoints = spec.endpointsByTag[tag.name] ?? []
            if (endpoints.length === 0) return null
            return (
              <div key={tag.name}>
                <H2 id={tag.name.toLowerCase()}>{tag.name}</H2>
                {tag.description && (
                  <p className="text-sm text-muted-foreground mb-4">{tag.description}</p>
                )}
                {endpoints.map(ep => (
                  <EndpointSection key={ep.operationId} endpoint={ep} />
                ))}
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
